import {Injectable, OnDestroy} from '@angular/core';

import {ConfigStub, IConfigStub} from "../../_typing/config-stub";
import {IDMap, ModelID} from "../../_typing/generic-types";
import {SeriesComponent} from "../../_models/series-component";
import {ApiService} from "../../services/api/api.service";
import {SearchQueryOptions} from "../../services/api/search-query-options";
import {
    getBaseFilter, getRelationWithIdFilter, getRelationWithManyIdsFilter
} from "../../services/api/filter_utils";
import {ListResponse} from "../../services/api/response-types";
import {mapTo, Observable, of, Subject} from "rxjs";
import {
    SeriesDataSeriesGroupDict,
    SeriesDataTableConfig,
    SeriesGroupConfig
} from "../../_typing/config/series-data-table";
import {concatMap, first, map, take, takeUntil, tap} from "rxjs/operators";
import {combineLatest} from "rxjs";
import {Process} from "../../_models/process";
import {Series} from "../../_models/series";
import {deepCopy, mapOrder, sortObjectsByProperty} from "../../lib/utils";

@Injectable()
export class SeriesDataTableFormService implements OnDestroy {
    private readonly onDestroy = new Subject<void>();
    processFilters: { [key: string]: any[] } = {};
    $fullSeriesList: Subject<Series[]> = new Subject();

    constructor(private api: ApiService) {
    }

    setDefaults(config: SeriesDataTableConfig): SeriesDataTableConfig {
        if (!config.column_formats) {
            config.column_formats = {};
        }
        if (!config.orientation) {
            config.orientation = 'vertical';
        }
        config = this._setProcessMapDefaults(config);
        return config;
    }

    private _setProcessMapDefaults(config: SeriesDataTableConfig): SeriesDataTableConfig {
        if (!config.process_sort_map) {
            config.process_sort_map = {};
        }
        config.process_list.forEach(process => {
            if (!config.process_sort_map?.[process.id]) {
                config.process_sort_map[process.id] = {sort: 'custom', all_series: false};
            }

            if (!config.process_series_map[process.id]) {
                config.process_series_map[process.id] = [];
            }
        })
        return config;
    }

    getSeriesLightFilters(processes) {
        processes.forEach(process => {
            if (!this.processFilters[process.id]) {
                this.processFilters[process.id] = this._seriesForProcessFilter(process.id);
            }
        })
        return this.processFilters;
    }

    private _seriesForProcessFilter(processId: ModelID): any[] {
        return [{
            name: 'series_components',
            op: 'any',
            val: {name: 'component', op: 'has', val: {name: 'id', op: 'eq', val: processId}}
        }]
    }

    processesWithSeriesFilter(): any[] {
        return [{
            "name": "series_components",
            "op": "any",
            "val": {
                "name": "series", op: "has", val: {
                    name: "id",
                    "op": "isnot",
                    "val": null
                }
            }
        }]
    }

    fullSeriesListSubjectConnect() {
        this.$fullSeriesList = new Subject();
    }

    getSeriesForProcesses(config: SeriesDataTableConfig, fullSeriesApi: boolean = false): Observable<SeriesDataSeriesGroupDict> {
        let keys: ModelID[] = [];
        const processIds = config.process_list.map(p => p.id);
        if (!processIds.length) return of(config.process_series_map);

        let $obs: Observable<SeriesGroupConfig[]>[] = [];

        for (let i = 0; i < processIds?.length; i++) {
            const processId = processIds[i];
            //Clean up config, old processes
            if (!config.process_list.map(p => p.id).includes(processId)) {
                keys.push(processId);
                continue;
            }

            config = this._setProcessMapDefaults(config);

            const options = new SearchQueryOptions();
            const selectedSeries: ModelID[] = config.process_series_map[processId].map(s => s.id);

            if (config.process_sort_map?.[processId]?.all_series) {
                options.filters = this._seriesForProcessFilter(processId);
            } else {
                let newFilter = [getBaseFilter(selectedSeries, 'id', 'in')];
                options.filters = this._seriesForProcessFilter(processId).concat(newFilter);
            }
            $obs.push(this._getSeriesForProcess(processId, options, config, fullSeriesApi).pipe(
                concatMap(() => this.seriesSelectionSort(processId, config)),
                take(1)
            ));
        }
        keys.forEach(key => delete config.process_series_map[key]);
        //Sort by process list
        const processSeriesSorted = {};//deepCopy(config.process_series_map);
        processIds.forEach(id => {
            processSeriesSorted[id] = config.process_series_map[id];
        })
        config.process_series_map = processSeriesSorted;
        return combineLatest($obs).pipe(map(() => {
            this.$fullSeriesList.complete();
            return config.process_series_map
        }));
    }

    private _getSeriesForProcess(processId: ModelID, options: SearchQueryOptions, config: SeriesDataTableConfig, fullSeriesApi: boolean = false): Observable<SeriesGroupConfig[]> {
        const apiModel = fullSeriesApi ? 'series' : 'series_light';
        return this.api[apiModel].searchMany(options).pipe(map(seriesList => {
            this.$fullSeriesList.next(seriesList.data);
            return this._updateSeriesProcessMap(processId, seriesList.data, config);
        }))
    }

    private _updateSeriesProcessMap(processId: ModelID, seriesList: Series[], config: SeriesDataTableConfig) {
        seriesList?.map((fullSeries: Series) => {
            const existingIndex = config.process_series_map[processId].findIndex(s => fullSeries.id === s.id);
            const updatedSeries = this.updateSeries(processId, existingIndex, config, fullSeries);
            if (existingIndex > -1) {
                config.process_series_map[processId][existingIndex] = updatedSeries;
            } else {
                config.process_series_map[processId].push(updatedSeries)
            }
        });
        //Remove deleted or unlinked series
        config.process_series_map[processId] = config.process_series_map[processId].filter(series => {
            return seriesList.findIndex(s => s.id === series.id) > -1;
        })
        return config.process_series_map[processId];
    }

    updateSeries(processId: ModelID, index: number, config: SeriesDataTableConfig, fullSeries: Series): Partial<Series> {
        const group = {group: index > -1 ? config.process_series_map[processId][index].group : ''}
        const configSeries = new ConfigStub<Series>(fullSeries) as Partial<Series>;
        return Object.assign({}, configSeries, group);
    }

    getMissingSeriesForProcess(processId: ModelID, config: SeriesDataTableConfig): Observable<SeriesGroupConfig[]> {
        const selectedSeries: ModelID[] = config.process_series_map[processId].map(s => s.id);
        const options = new SearchQueryOptions();
        let newFilter = [{not: getBaseFilter(selectedSeries, 'id', 'in')}];
        options.filters = this._seriesForProcessFilter(processId).concat(newFilter);

        return this.api.series_light.searchMany(options).pipe(first(), takeUntil(this.onDestroy),
            map(seriesList => {
                seriesList.data?.map((s: Series) => {
                    const group = {group: ''}
                    const configSeries = new ConfigStub<Series>(s) as Partial<Series>;
                    config.process_series_map[processId] = config.process_series_map[processId].concat(Object.assign({}, configSeries, group));
                });

                return config.process_series_map[processId];
            }))
    }

    getSeriesReportConfig(processId: ModelID, seriesIds: ModelID[]): Observable<ListResponse<Partial<SeriesComponent>>> {
        const options = new SearchQueryOptions();
        options.filters = [
            getRelationWithIdFilter('component', processId),
            getRelationWithManyIdsFilter('series', seriesIds)
        ];

        return this.api.series_component_order.searchMany(options)
    }

    seriesSelectionSort(processId: ModelID, config: SeriesDataTableConfig): Observable<SeriesGroupConfig[]> {
        let $obs: Observable<null | ListResponse<Partial<SeriesComponent>>> = of(null);
        if (config.process_sort_map?.[processId]?.sort === 'report') {
            const seriesIds = config.process_series_map[processId]?.map(s => s.id);
            if (seriesIds?.length) {
                $obs = this.getSeriesReportConfig(processId, seriesIds)
            }
        }
        let seriesComponentDict: IDMap<Partial<SeriesComponent>[]> = {};
        return $obs.pipe(first(), map(response => {
            if (response?.['data']) {
                seriesComponentDict[processId] = response?.['data'];
                this.applySort(processId, config, seriesComponentDict[processId])
            } else {
                this.applySort(processId, config);
            }
            return config.process_series_map[processId];
        }));
    }

    applySort(processId: ModelID, config: SeriesDataTableConfig, reportConfig?: Partial<SeriesComponent>[]) {
        if (!config.process_series_map[processId]) return;

        if (['name', 'alias', 'description'].includes(config.process_sort_map[processId]?.sort)) {
            sortObjectsByProperty(config.process_series_map[processId], config.process_sort_map[processId].sort, null, true);
            return;
        }

        if (reportConfig) {
            const result: string[] = reportConfig
                .slice()
                .sort((a, b) => {
                    const orderA = a.attributes.series_order;
                    const orderB = b.attributes.series_order;

                    if (orderA === null && orderB === null) return 0;
                    if (orderA === null) return 1; // nulls last
                    if (orderB === null) return -1;
                    return orderA - orderB;
                })
                .map(item => item.relationships.series.data?.id)
                .filter((id): id is string => !!id);
            config.process_series_map[processId] = mapOrder(config.process_series_map[processId], result, 'id', true)
        }
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
